import type { ModuleConfig } from "../moduleConfig.types";
import {
  AI_VISIBILITY,
  BRANDS,
  MARKETS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  TRENDS_METRIC,
  VIEW_ID,
  CATEGORY,
  HUMAN_METRIC,
  MODEL,
} from "@/constant/constantsThrougoutProject";

const STAT_TESTING = [
  { id: "POP", enabled: true, activeLabel: "Period-P" },
  {
    id: "YOY",
    enabled: false,
    activeLabel: "Year-Y",
    disabledMessage: "Year-over-Year comparison is not available for AI Visibility.",
    fallbackTo: "POP",
  },
  { id: "PTP", hidden: true, enabled: false, activeLabel: "Peer-P", fallbackTo: "POP" },
];

const BAR_STAT_TESTING = [
  {
    id: "POP",
    enabled: false,
    activeLabel: "Period-P",
    disabledMessage: "Period-over-Period comparison is not available on this screen.",
    fallbackTo: "NONE",
  },
  {
    id: "YOY",
    enabled: false,
    activeLabel: "Year-Y",
    disabledMessage: "Year-over-Year comparison is not available on this screen.",
    fallbackTo: "NONE",
  },
  { id: "PTP", enabled: false, activeLabel: "Peer-P", fallbackTo: "NONE" },
];

const PRE_POST_STAT_TESTING = [
  { id: "POP", enabled: true, activeLabel: "Period-P" },
  {
    id: "YOY",
    enabled: false,
    activeLabel: "Year-Y",
    disabledMessage: "Year-over-Year comparison is not available on this screen.",
    fallbackTo: "POP",
  },
  { id: "PTP", hidden: true, enabled: false, activeLabel: "Peer-P", fallbackTo: "POP" },
];

const COMMON_URL_KEYS = [
  MARKETS,
  BRANDS,
  CATEGORY,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_TYPE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  VIEW_ID,
];

/**
 * Hierarchical screen config for the AI Visibility module.
 *
 * Screen types: BRAND (By Brand), MODEL (By Model).
 * Visualisation: GAUGE replaces BARCODE from Brand Visibility.
 */
export const aiVisibilityModuleConfig: ModuleConfig = {
  config: {
    type: "MODULE",
    module: AI_VISIBILITY,
    supportMarkets: true,
    isMarketsDisabled: true,
    supportedMarketIdentities: ["US"],
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    maximumAllowedMarkets: 1,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    isBrandsFilteredByCategory: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportBase: false,
    defaultBase: "GENPOP",
    supportedBases: ["GENPOP"],
    supportDatePeriod: true,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["GAUGE", "BAR", "TABLE"],
    defaultVisualisationType: "GAUGE",
    supportCategories: true,
    apiSendCategories: true,
    defaultCategory: 0,
    supportHumanMetrics: true,
    supportHumanMetricsToggle: true,
    maximumAllowedHumanMetrics: 1,
    // Fallback when nothing is selected — awareness is the first conversion
    // funnel metric (same defaultTrendsMetric as conversion.config.ts).
    defaultHumanMetrics: ["awareness"],
    // Metrics resolve from the shared catalog's `ai_visibility` module
    // (nested Recommends breakdown keys are stripped on ingest).
    supportFilters: false,
    supportScoringControls: false,
    statTestingConfig: STAT_TESTING,
    // AI Visibility data is only available from August 1, 2026 onwards.
    // warningMessage is shown in the page header when a selected range is
    // clamped to this bound (same pattern as reputation / visibility).
    dateBounds: {
      min: "2026-08-01",
      max: null,
      warningMessage:
        "Data for the previous time period comparison is only available from August 1, 2026.",
    },
    postDateBounds: {
      min: "2026-08-01",
      max: null,
      warningMessage:
        "Data for the previous time period comparison is only available from August 1, 2026.",
    },
  },

  screenTypes: {
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        maximumAllowedMarkets: 1,
        apiGroupedBy: "BRAND",
        apiSendFilters: false,
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
        supportHumanMetrics: false,
        maximumAllowedHumanMetrics: 1,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            defaultPeriod: "P_W",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
            supportVisualisationSwitch: true,
            supportTrendsMetric: true,
            defaultTrendsMetric: "ai_visibility_score",
            showTrendsMetricDropdown: true,
            // Same model filter as POINT_IN_TIME ("All Model" = null).
            supportAiModel: true,
            supportedURLKeys: [
              ...COMMON_URL_KEYS,
              PERIOD,
              TRENDS_METRIC,
              MODEL,
            ],
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedVisualisationTypes: ["GAUGE", "BAR", "TABLE"],
            defaultVisualisationType: "GAUGE",
            // Model + human-metric pickers alongside the brand selector —
            // "All Model" (null) is a valid choice here, unlike By-Model
            // where a specific model is required.
            supportAiModel: true,
            supportHumanMetrics: true,
            // GAUGE stays single-select; BAR/TABLE lift the cap below.
            maximumAllowedHumanMetrics: 1,
            supportHumanMetricsToggle: false,
            defaultHumanMetrics: ["awareness"],
            supportedURLKeys: [...COMMON_URL_KEYS, MODEL, HUMAN_METRIC],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: BAR_STAT_TESTING,
                maximumAllowedHumanMetrics: null,
              },
            },
            TABLE: {
              config: { maximumAllowedHumanMetrics: null },
            },
          },
        },
        PRE_POST: {
          config: {
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            // Multi-brand Pre/Post: one AI metric, brands on the category
            // axis with Pre + Post bars per brand (mirrors TREND's metric
            // picker + EntityList layout).
            supportedVisualisationTypes: ["BAR", "TABLE"],
            defaultVisualisationType: "BAR",
            supportVisualisationSwitch: true,
            supportTrendsMetric: true,
            defaultTrendsMetric: "ai_visibility_score",
            showTrendsMetricDropdown: true,
            supportAiModel: true,
            supportHumanMetrics: false,
            supportedURLKeys: [
              ...COMMON_URL_KEYS,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              MODEL,
              TRENDS_METRIC,
            ],
            statTestingConfig: PRE_POST_STAT_TESTING,
          },
        },
      },
    },

    MODEL: {
      config: {
        type: "SCREEN_TYPE",
        maximumAllowedMarkets: 1,
        maximumAllowedBrands: 1,
        // Models are the row axis — payload maps this to groupedBy: ["BRAND", "AI_MODEL"].
        apiGroupedBy: "MODEL",
        apiSendFilters: false,
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
        supportHumanMetrics: false,
      },
      modes: {
        TREND: {
          config: {
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            defaultPeriod: "P_W",
            supportedVisualisationTypes: ["LINE", "TABLE"],
            defaultVisualisationType: "LINE",
            supportVisualisationSwitch: true,
            supportTrendsMetric: true,
            defaultTrendsMetric: "ai_visibility_score",
            showTrendsMetricDropdown: true,
            // Multi-select model filter — same as POINT_IN_TIME.
            supportAiModel: true,
            aiModelIsMultiSelect: true,
            supportedURLKeys: [
              ...COMMON_URL_KEYS,
              PERIOD,
              TRENDS_METRIC,
              MODEL,
            ],
          },
        },
        POINT_IN_TIME: {
          config: {
            apiViewMode: "POINT_IN_TIME",
            supportedVisualisationTypes: ["GAUGE", "BAR", "TABLE"],
            defaultVisualisationType: "GAUGE",
            // Multi-select model filter (AiModelDropdown) — narrows which
            // model cards are rendered (see useEntityState's MODEL selectedIds)
            // — plus the human-metric picker, mirroring By-Brand's own
            // POINT_IN_TIME pickers.
            supportAiModel: true,
            aiModelIsMultiSelect: true,
            supportHumanMetrics: true,
            // GAUGE stays single-select; BAR/TABLE lift the cap below.
            maximumAllowedHumanMetrics: 1,
            supportHumanMetricsToggle: false,
            defaultHumanMetrics: ["awareness"],
            supportedURLKeys: [...COMMON_URL_KEYS, MODEL, HUMAN_METRIC],
          },
          visualisationType: {
            BAR: {
              config: {
                statTestingConfig: BAR_STAT_TESTING,
                maximumAllowedHumanMetrics: null,
              },
            },
            TABLE: {
              config: { maximumAllowedHumanMetrics: null },
            },
          },
        },
        PRE_POST: {
          config: {
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            maximumAllowedBrands: 1,
            supportedVisualisationTypes: ["BAR", "TABLE"],
            defaultVisualisationType: "BAR",
            supportVisualisationSwitch: true,
            // Single-select model picker in the page header (same control as
            // POINT_IN_TIME, but one model at a time — required for the
            // By-Model PRE_POST BAR chart).
            supportAiModel: true,
            supportedURLKeys: [
              ...COMMON_URL_KEYS,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              MODEL,
            ],
            statTestingConfig: PRE_POST_STAT_TESTING,
          },
        },
      },
    },
  },
};
